import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/context/AuthContext';
import { API_ENDPOINTS, getApiUrl } from '@/utils/api-config';
import { apiGet } from '@/utils/api-interceptor';

export interface GrowthDatum {
  label: string;
  businesses: number;
  users: number;
}

export interface GrowthDataSections {
  daily?: GrowthDatum[];
  weekly?: GrowthDatum[];
  monthly?: GrowthDatum[];
  yearly?: GrowthDatum[];
}

export interface ConversationDatum {
  week: string;
  conversations: number;
}

export type RevenueValue = number | {
  source?: string | number;
  parsedValue?: number;
};

export interface RevenueDatum {
  month: string;
  revenue?: RevenueValue;
  subscriptions?: RevenueValue;
  add_on?: RevenueValue;
  add_on_agents?: RevenueValue;
  topups?: RevenueValue;
}

export interface BusinessTeamDatum {
  month: string;
  active: number;
  inactive: number;
  new: number;
}
export interface Stats {
  total_businesses: number;
  active_agents: number;
  total_conversations: number;
  monthly_revenue: number;
}

export interface SuperAdminDashboardData {
  growthData: GrowthDataSections;
  conversationData: ConversationDatum[];
  revenueData: RevenueDatum[];
  businessTeamMembersData: BusinessTeamDatum[];
  stats: Stats;
}

interface SuperAdminDashboardResponse {
  message: string;
  data: SuperAdminDashboardData;
  status: string;
}

export const useDashboard = () => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['superadmin-dashboard-overview'],
    queryFn: async (): Promise<SuperAdminDashboardData> => {
      const response = await apiGet(getApiUrl(API_ENDPOINTS.SUPERADMIN_DASHBOARD_OVERVIEW));

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch dashboard data');
      }

      const payload: SuperAdminDashboardResponse = await response.json();
      return payload.data;
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};
